import ExpandableText from '@/components/ExpandableText';
import InformationTextsModal from '@/components/InformationTextsModal';
import TicketViewerModal from '@/components/manage/tickets/viewer/TicketViewerModal';
import PriceDisplay from '@/components/PriceDisplay';
import Changeability from '@/components/purchase/card/Changeability';
import Refundability from '@/components/purchase/card/Refundability';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO, LegDTO } from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import {
  areAllAdmissionsWithValue,
  areRefundedLike,
  isAdmissionInactive,
} from '@/utils/admission';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import {
  Button,
  Icons,
  Typography,
  typographyVariants,
} from '@packages/webshop-design-system';
import _uniq from 'lodash/uniq';
import { useMemo, useState, type FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface ProductDetailsProps {
  leg: LegDTO;
  admissions: Array<AdmissionDTO>;
  passengersMap: CheckoutOrBookingPassengerMap;
}

const ProductDetails: FC<ProductDetailsProps> = ({
  leg,
  admissions,
  passengersMap,
}) => {
  const [isTicketViewerOpen, setIsTicketViewerOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const areAllRefundable = areAllAdmissionsWithValue(
    admissions,
    'refundable',
    'YES'
  );
  const areAllExchangeable = areAllAdmissionsWithValue(
    admissions,
    'exchangeable',
    'YES'
  );
  const refundedLike = areRefundedLike(admissions);

  // Tickets are only viewable for admissions still in effect: refunded /
  // exchanged / released / cancelled admissions no longer entitle travel,
  // so their tickets must not be presented as scannable.
  const activeAdmissions = useMemo(
    () => admissions.filter((admission) => !isAdmissionInactive(admission)),
    [admissions]
  );

  const passengerCount = useMemo(() => {
    const fulfilledPassengerIds = admissions
      .filter((admission) =>
        (admission.fulfillments || []).some((f) => f.status === 'FULFILLED')
      )
      .flatMap((admission) => admission.passengerIds || []);
    return _uniq(fulfilledPassengerIds).length;
  }, [admissions]);

  const admissionsAmount = useMemo(() => {
    return admissions.reduce((sum, admission) => {
      if (admission.status === 'REFUNDED') {
        return sum;
      }

      const ancillariesAmount = admission.ancillaries?.reduce(
        (total, ancillary) =>
          total + (ancillary.price?.amount || 0) * (ancillary.amountOf || 1),
        0
      );
      return sum + (admission.price?.amount || 0) + (ancillariesAmount || 0);
    }, 0);
  }, [admissions]);

  const description = admissions[0].productDescription;
  // Same selection as the sales flow's offer card: only INFORMATION texts, all
  // of them, so the modal can stack them (BR-57160).
  const descriptions = useMemo(
    () =>
      (admissions[0]?.productDescriptiveTexts ?? [])
        .filter(
          (text) => text.textElementType === 'INFORMATION' && !!text.description
        )
        .map((text) => text.description as string),
    [admissions]
  );

  return (
    <div className="flex flex-col gap-5 laptop:gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between laptop:items-end laptop:pb-0">
          <div className="flex items-center gap-2 px-2">
            <Typography variant="body1-bold">
              {description}
              {!refundedLike && (
                <span
                  className={cn(
                    typographyVariants({ variant: 'body2' }),
                    'ml-2 whitespace-nowrap align-middle text-neutral'
                  )}
                >
                  <TransText
                    i18nKey="passengersCount"
                    values={{ count: passengerCount }}
                  />
                </span>
              )}
            </Typography>
          </div>
          {!refundedLike && (
            <Typography variant="body1-bold" className="text-primary">
              <PriceDisplay amount={admissionsAmount} />
            </Typography>
          )}
        </div>
        <ExpandableText
          text={descriptions[0]}
          className="px-2"
          renderHtml
          onReadMore={() => setIsInfoModalOpen(true)}
        />
        <InformationTextsModal
          title={description}
          // Mirror the header, which hides the price for refunded-like
          // admissions; `amount` is optional so the modal just omits it.
          amount={refundedLike ? undefined : admissionsAmount}
          descriptions={descriptions}
          isOpen={isInfoModalOpen}
          onOpenChange={setIsInfoModalOpen}
          data-testid="product-details-information-modal"
        />
        <div className="flex gap-3 px-2">
          <Changeability condition={areAllExchangeable} />
          <Refundability condition={areAllRefundable} />
        </div>
      </div>
      {activeAdmissions.length > 0 && (
        <Button
          data-testid="product-details-view-tickets"
          size={isLaptopOrBigger ? 'regular' : 'medium'}
          variant="secondary"
          className="laptop:ml-auto laptop:w-1/2"
          onClick={() => setIsTicketViewerOpen(true)}
        >
          <Icons.ticket width={20} height={20} />
          <TransText i18nKey="viewTickets" />
        </Button>
      )}
      <TicketViewerModal
        leg={leg}
        admissions={activeAdmissions}
        passengersMap={passengersMap}
        isOpen={isTicketViewerOpen}
        onOpenChange={setIsTicketViewerOpen}
      />
    </div>
  );
};

export default ProductDetails;
